import { useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Loader2, Mic, Square } from 'lucide-react';

const MAX_SECONDS = 30;

// Khaya's Twi recogniser wants WAV, so re-encode the recording as 16 kHz mono PCM.
const toWav = async (blob) => {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const decoded = await ctx.decodeAudioData(await blob.arrayBuffer());
  ctx.close();
  const rate = 16000;
  const offline = new OfflineAudioContext(1, Math.ceil(decoded.duration * rate), rate);
  const src = offline.createBufferSource();
  src.buffer = decoded;
  src.connect(offline.destination);
  src.start();
  const pcm = (await offline.startRendering()).getChannelData(0);
  const buf = new ArrayBuffer(44 + pcm.length * 2);
  const v = new DataView(buf);
  const w = (o, str) => [...str].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  w(0, 'RIFF'); v.setUint32(4, 36 + pcm.length * 2, true); w(8, 'WAVE'); w(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, rate, true); v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  w(36, 'data'); v.setUint32(40, pcm.length * 2, true);
  pcm.forEach((x, i) => v.setInt16(44 + i * 2, Math.max(-1, Math.min(1, x)) * 0x7fff, true));
  return new Blob([buf], { type: 'audio/wav' });
};

export const VoiceButton = ({ onText, disabled, language = 'en' }) => {
  const [state, setState] = useState('idle');
  const recRef = useRef(null);
  const timerRef = useRef(null);

  const upload = async (blob) => {
    setState('busy');
    const form = new FormData();
    form.append('language', language);
    try {
      if (language === 'tw') form.append('file', await toWav(blob), 'voice.wav');
      else form.append('file', blob, blob.type.includes('mp4') ? 'voice.mp4' : 'voice.webm');
      const { data } = await api.post('/duti/transcribe', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data.text) onText(data.text, data.original);
      else toast.error("I didn't catch that — please try again.");
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not transcribe that');
    } finally {
      setState('idle');
    }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearTimeout(timerRef.current);
        upload(new Blob(chunks, { type: rec.mimeType || 'audio/webm' }));
      };
      rec.start();
      recRef.current = rec;
      timerRef.current = setTimeout(() => rec.state === 'recording' && rec.stop(), MAX_SECONDS * 1000);
      setState('recording');
    } catch {
      toast.error('Microphone access is needed for voice requests');
    }
  };

  const stop = () => recRef.current?.state === 'recording' && recRef.current.stop();

  return (
    <Button
      size="icon"
      variant={state === 'recording' ? 'destructive' : 'ghost'}
      className={`h-9 w-9 shrink-0 ${state === 'recording' ? 'animate-pulse' : ''}`}
      disabled={disabled || state === 'busy'}
      onClick={state === 'recording' ? stop : start}
      aria-label={state === 'recording' ? 'Stop recording' : language === 'tw' ? 'Speak a request in Twi' : 'Speak a request'}
      data-testid="ai-bubble-voice-btn"
      data-state={state}
      data-lang={language}
    >
      {state === 'busy' ? <Loader2 className="h-4 w-4 animate-spin" /> : state === 'recording' ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
    </Button>
  );
};
